import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { getPosts } from "@/api";
import { useSeo } from "@/seo";
import { Reveal, SectionHead, Eyebrow } from "@/components/Reveal";

const CATEGORIES = ["All", "UAE Tax", "Transfer Pricing", "GCC", "Family Office", "India UAE"];
const COVER = "https://images.unsplash.com/photo-1743385779313-ac03bb0f997b?crop=entropy&cs=srgb&fm=jpg&q=80&w=900";

export default function Insights() {
  const [posts, setPosts] = useState([]);
  const [category, setCategory] = useState("All");
  useSeo({
    title: "Insights, UAE Tax, Transfer Pricing, GCC & Family Office, TruVerse Middle East",
    description: "Analysis on UAE corporate tax, transfer pricing, India capability centres and family office structures from the TruVerse corridor desk.",
  });
  useEffect(() => {
    getPosts().then(setPosts).catch(() => {});
  }, []);

  const filtered = useMemo(
    () => (category === "All" ? posts : posts.filter((p) => p.category === category)),
    [posts, category]
  );

  return (
    <div data-testid="insights-page">
      <section className="bg-ink py-24 lg:py-28">
        <div className="container-site max-w-4xl">
          <Reveal>
            <Eyebrow>Insights</Eyebrow>
            <h1 className="mt-6 text-4xl leading-tight tracking-tight md:text-6xl">From the corridor desk.</h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-ink-muted md:text-lg">
              Short, precise notes on UAE tax, transfer pricing, India capability centres and family office structures.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-24">
        <div className="container-site">
          <div className="flex flex-wrap gap-3" role="tablist" aria-label="Filter by category">
            {CATEGORIES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCategory(c)}
                data-testid={`insights-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={`rounded-sm border px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] transition-colors duration-200 ${
                  category === c
                    ? "border-accent bg-accent text-accent-foreground"
                    : "border-border text-muted-foreground hover:border-accent hover:text-accent"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="mt-14 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((p, i) => (
              <Reveal key={p.doc_id} delay={i * 0.06}>
                <Link to={`/insights/${p.slug}`} data-testid={`insights-card-${p.slug}`} className="group flex h-full flex-col border border-border bg-card transition-all duration-300 hover:-translate-y-1 hover:border-accent">
                  <img src={COVER} alt="" className="h-44 w-full object-cover" loading="lazy" />
                  <div className="flex flex-1 flex-col p-8">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent">{p.category}</p>
                    <h2 className="mt-4 text-xl leading-snug text-foreground">{p.title}</h2>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{p.excerpt}</p>
                    <span className="mt-6 inline-flex items-center text-xs font-semibold uppercase tracking-[0.2em] text-accent">
                      Read <ArrowRight size={14} className="ml-2 transition-transform duration-200 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
            {filtered.length === 0 && (
              <p className="col-span-full py-16 text-sm text-muted-foreground" data-testid="insights-empty">No posts in this category yet.</p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
